<template>
  <div class="register-tab">
    <SalesRegisterToolbar
        ref="toolbarRef"
        :filters="appliedFilters"
        :chip-meta="filterChipMeta"
        @go="onClickGo"
        @open-filter="onOpenFilter"
        @apply="onApplyFilters"
        @clear="onClearFilters"
        @remove-chip="onRemoveChip"
        @print="onClickToPrint"
        @export="exportTable"
    >
      <div class="col-12 col-sm-6">
        <label class="filter-label">Product/Service Type</label>
        <v-select
            placeholder="Select Product/Service Type"
            v-model="draftFilters.product_type"
            :options="productTypes"
            label="text"
            @update:modelValue="onChangeProductType"
        />
      </div>
      <div class="col-12 col-sm-6">
        <label class="filter-label">Product Group</label>
        <AsyncSelect
            placeholder="Select Product Group"
            :api-service="fetchProductList"
            v-model="draftFilters.product_group"
            label="text"
            :additional-query="groupQueryObj"
            :format-label="option => option.text"
            :option-selected="onChangeProductGroup"
            ref="groupSelector"
        />
      </div>
      <div class="col-12 col-sm-6">
        <label class="filter-label">Product/Service</label>
        <AsyncSelect
            placeholder="Select Product/Service"
            :api-service="fetchProductList"
            v-model="draftFilters.product"
            label="text"
            :additional-query="productQueryObj"
            :format-label="option => option.text"
            ref="productSelector"
        />
      </div>
      <div class="col-12 col-sm-6">
        <label class="filter-label">Referral</label>
        <AsyncSelect
            placeholder="Select Referrer"
            v-model="draftFilters.referral"
            :api-service="fetchContactProfiles"
            :additional-query="{ type: 'agent' }"
            :format-label="formatPatientLabel"
        />
      </div>
      <div class="col-12 col-sm-6">
        <label class="filter-label">Project</label>
        <v-select
            placeholder="Select Project"
            v-model="draftFilters.project"
            :options="projectList"
            label="name"
        />
      </div>
      <div class="col-12 col-sm-6">
        <label class="filter-label">Bill Type</label>
        <v-select
            multiple
            placeholder="Select Bill Type"
            v-model="draftFilters.bill_type"
            :options="billTypeList"
            label="label"
        />
      </div>
      <div class="col-12 col-sm-6">
        <div class="d-flex align-items-center justify-content-between">
          <label class="filter-label">Party</label>
          <span class="filter-search-icon" @click="onOpenContactSearchModal">
            <i class="fas fa-search"></i>
          </span>
        </div>
        <AsyncSelect
            placeholder="Select Party"
            v-model="draftFilters.party"
            :api-service="fetchContactProfiles"
            :additional-query="additionalContactOption"
            :format-label="formatPatientLabel"
        />
      </div>
      <div class="col-12 col-sm-6">
        <label class="filter-label">Manufacturer / Supplier</label>
        <AsyncSelect
            placeholder="Supplier Name, ID, Mobile No"
            v-model="draftFilters.supplier"
            :api-service="fetchContactProfiles"
            :additional-query="{ type: 'supplier' }"
            :format-label="formatPatientLabel"
        />
      </div>
    </SalesRegisterToolbar>
    <ListTable
        :tableItems="investigations"
        :currentPage="itemData && itemData.data && itemData.data.current_page"
        :lastPage="itemData && itemData.data && itemData.data.last_page"
        :perPage="itemData && itemData.data && itemData.data.per_page"
        :totalValue="itemData && itemData.total"
        :isLoaded="hasLoaded"
    />
    <div class="mt-2">
      <Pagination :offset="offset" @onPageChange="onPageChange"/>
    </div>
    <ContactDropdownFilter
        v-if="$store.state.isModalOpenThree"
        @onSearchContact="onSearchContact"
        type="customer"
    />
    <Loader v-if="loading"/>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import Pagination from '@/components/atom/Pagination'
import Loader from '@/components/atom/LoaderComponent.vue'
import ContactDropdownFilter from '@/components/atom/Contact/ContactDropdownFilter.vue'
import ListTable from '@/components/molecule/company/inventory/product-sales-register/ItemTabTable.vue'
import SalesRegisterToolbar from '@/components/molecule/company/inventory/product-sales-register/SalesRegisterToolbar.vue'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import handlePurchase from '@/services/modules/purchase'
import handleHospitalReport from '@/services/modules/hospital/report'
import handleContact from '@/services/modules/contact'
import handleCompany from '@/services/modules/company'
import { useAsyncDropdownHelper } from '@/services/utils/asyncDropdownHelper'
import itemPrinter from '@/services/utils/pdf/saleRegisterItem'

const props = defineProps({
  projectList: {
    type: Array,
    default: () => []
  },
  homeData: {
    type: Object,
    default: () => ({})
  }
})

const $router = useRouter()
const $route = useRoute()
const $store = useStore()
const showError = inject('showError')

const { fetchInvestigationInvoiceReport } = handleHospitalReport()
const { fetchProductList } = handlePurchase()
const { fetchContactProfiles } = handleContact()
const { fetchCompanyInfo } = handleCompany()
const { formatPatientLabel } = useAsyncDropdownHelper()
const { exportToPDF } = itemPrinter()

const loading = ref(false)
const hasLoaded = ref(false)
const investigations = ref([])
const itemData = ref({})
const companyInfo = ref({})
const toolbarRef = ref(null)
const productSelector = ref(null)
const groupSelector = ref(null)
const offset = 20

const additionalContactOption = {
  with_mobile: 1,
  dealer_id: '',
  roles: 'customer'
}

const filterChipMeta = [
  { key: 'product_type', label: 'Product Type', text_key: 'text' },
  { key: 'product_group', label: 'Product Group', text_key: 'text' },
  { key: 'product', label: 'Product', text_key: 'text' },
  { key: 'referral', label: 'Referral', text_key: 'name' },
  { key: 'project', label: 'Project', text_key: 'name' },
  { key: 'bill_type', label: 'Bill Type', text_key: 'label' },
  { key: 'party', label: 'Party', text_key: 'name' },
  { key: 'supplier', label: 'Supplier', text_key: 'name' }
]

const getEmptyFilters = () => {
  return {
    product_type: null,
    product_group: null,
    product: null,
    referral: null,
    project: null,
    bill_type: [],
    party: null,
    supplier: null
  }
}

const cloneFilters = (filters) => {
  return Object.assign({}, filters, { bill_type: filters.bill_type.slice() })
}

const appliedFilters = ref(getEmptyFilters())
const draftFilters = ref(getEmptyFilters())

const companyId = computed(() => $route.params.companyId)
const start = computed(() => $route.query.start)
const end = computed(() => $route.query.end)
const page = computed(() => $route.query.page)
const token = computed(() => localStorage.getItem('token'))

const productTypes = computed(() => {
  const types = props.homeData.product_type
  if (! types) return []
  return Object.keys(types).map(key => {
    return { id: key, text: types[key] }
  })
})

const billTypeList = computed(() => {
  const invoiceTypes = props.homeData.hospital_invoice_types
  if (! invoiceTypes) return []
  return Object.keys(invoiceTypes).map(value => {
    return { value: value, label: invoiceTypes[value] }
  })
})

const productQueryObj = computed(() => {
  const filters = draftFilters.value
  const queryObj = {}
  if (filters.product_type) queryObj.product_type = filters.product_type.id
  if (filters.product_group) queryObj.ancestor_id = filters.product_group.id
  return queryObj
})

const groupQueryObj = computed(() => {
  const queryObj = {
    group_level: 1
  }
  if (draftFilters.value.product_type) queryObj.product_type = draftFilters.value.product_type.id
  return queryObj
})

const getQuery = () => {
  const filters = appliedFilters.value
  let query = '?company_id=' + companyId.value
  if (filters.bill_type.length) query += '&sale_type=' + filters.bill_type.map(option => option.value).join(',')
  if (filters.party) query += '&contact_profile_id=' + filters.party.id
  if (filters.supplier) query += '&supplier_id=' + filters.supplier.id
  if (filters.product) query += '&product_id=' + filters.product.id
  if (filters.product_type) query += '&product_type=' + filters.product_type.id
  if (filters.product_group) query += '&ancestor_id=' + filters.product_group.id
  if (filters.referral) query += `&referral_id=${filters.referral.id}`
  if (filters.project) query += '&project_id=' + filters.project.id
  if (start.value) query += '&start_date=' + start.value
  if (end.value) query += '&end_date=' + end.value
  if (page.value) query += '&page=' + page.value
  query += '&offset=' + offset
  return query
}

const getPrintParams = () => {
  const filters = appliedFilters.value
  return {
    project: !! filters.project,
    contact_profile: !! filters.party,
    supplier: !! filters.supplier,
    product_type: !! filters.product_type,
    product_id: !! filters.product
  }
}

const setPagination = (data) => {
  $store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page
  })
}

const resetTable = () => {
  investigations.value = []
  itemData.value = {}
  $store.commit('resetPagination')
}

const investigationInvoiceList = async () => {
  loading.value = true
  try {
    const res = await fetchInvestigationInvoiceReport(getQuery())
    if (! res.status) {
      resetTable()
      return
    }
    investigations.value = res.data.data
    itemData.value = res
    setPagination(res.data)
  } catch (err) {
    showError('Something went wrong')
  } finally {
    hasLoaded.value = true
    loading.value = false
  }
}

const resetPage = async () => {
  $store.commit('resetPagination')
  await $router.push({ path: $route.path, query: Object.assign({}, $route.query, { page: 1 }) })
}

const onClickGo = async () => {
  await resetPage()
  await investigationInvoiceList()
}

const onPageChange = async (page) => {
  const routeQuery = Object.assign({}, $route.query)
  routeQuery.page = page
  await $router.push({ path: $route.path, query: routeQuery })
  await investigationInvoiceList()
}

const onOpenFilter = () => {
  draftFilters.value = cloneFilters(appliedFilters.value)
}

const onApplyFilters = async () => {
  appliedFilters.value = cloneFilters(draftFilters.value)
  await onClickGo()
}

const onClearFilters = async () => {
  appliedFilters.value = getEmptyFilters()
  draftFilters.value = getEmptyFilters()
  await onClickGo()
}

const onRemoveChip = async (key) => {
  appliedFilters.value[key] = getEmptyFilters()[key]
  await onClickGo()
}

const clearSelector = (selector) => {
  if (! selector.value) return
  selector.value.clear()
}

const onChangeProductType = () => {
  draftFilters.value.product_group = null
  draftFilters.value.product = null
  clearSelector(groupSelector)
  clearSelector(productSelector)
}

const onChangeProductGroup = () => {
  draftFilters.value.product = null
  clearSelector(productSelector)
}

const onOpenContactSearchModal = () => {
  $store.state.isModalOpenThree = true
}

const onSearchContact = (contact) => {
  const draft = Object.assign({}, draftFilters.value, { party: contact })
  if (toolbarRef.value) toolbarRef.value.openFilter()
  draftFilters.value = draft
}

const onClickToPrint = async () => {
  loading.value = true
  try {
    const res = await fetchInvestigationInvoiceReport(getQuery() + '&is_all=true')
    if (! res.status) {
      showError('No data found')
      return
    }
    exportToPDF(companyInfo.value, res, true, false, start.value || '', end.value || '', getPrintParams())
  } catch (err) {
    showError('Something went wrong')
  } finally {
    loading.value = false
  }
}

const exportTable = () => {
  const url = `${process.env.VUE_APP_BASE_URL}/export/reports/investigation-invoice-report${getQuery()}&_token=${token.value}&is_export=1&is_item=1`
  const anchor = document.createElement('a')
  anchor.setAttribute('href', url)
  anchor.click()
}

const setCompanyInfo = async () => {
  try {
    const res = await fetchCompanyInfo(companyId.value)
    companyInfo.value = res.data
  } catch (err) {
    showError('Failed to load company info')
  }
}

onMounted(() => {
  setCompanyInfo()
})
</script>
